var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
/* Same literal as the embed API; a type-only import avoids a runtime cycle. */
var api = "piab-embed/v1";
var ERROR_LIMIT = 20;
var ERROR_MESSAGE_LIMIT = 300;
var STATS_INTERVAL_MS = 15000;
var REPLAY_BUFFER_LIMIT = 50;
function makeIframeId() {
    var c = typeof crypto !== "undefined" ? crypto : undefined;
    if (c === null || c === void 0 ? void 0 : c.randomUUID) {
        return c.randomUUID().slice(0, 8);
    }
    return Math.random().toString(36).slice(2, 10);
}
/** Random id chosen once per document load; also sent in `ready`. */
export var telemetryIframeId = makeIframeId();
var installed = false;
var quality = "high";
var app;
var errorsEmitted = 0;
var firstFrameEmitted = false;
var sceneLoadedEmitted = false;
var contextLost = false;
/** Draw calls of the most recently rendered frame (see `attachTelemetryApp`). */
var lastDrawCalls;
var statsTimer;
var replayBuffer = [];
function msSinceBoot() {
    return Math.round(performance.now());
}
function post(message, target) {
    var _a;
    var destination = (_a = target) !== null && _a !== void 0 ? _a : (window.parent !== window ? window.parent : undefined);
    if (!destination) {
        /* Not framed (plain dev tab): keep it visible for manual checks. */
        console.debug("[telemetry]", message.name, message.data);
        return;
    }
    try {
        /* PoC: target origin "*" per CONTRACT.md. */
        destination.postMessage(message, "*");
    }
    catch (error) {
        console.debug("[telemetry] post failed", error);
    }
}
/** Post one telemetry event to the parent. No-op unless installed. */
export function emitTelemetry(name, data) {
    if (data === void 0) { data = {}; }
    if (!installed) {
        return;
    }
    var message = {
        api: api,
        type: "telemetry",
        iframeId: telemetryIframeId,
        name: name,
        t: Date.now(),
        data: data,
    };
    if (name !== "iframe.stats") {
        replayBuffer.push(message);
        if (replayBuffer.length > REPLAY_BUFFER_LIMIT) {
            replayBuffer.shift();
        }
    }
    post(message);
}
/** Re-send the buffered (non-stats) events, flagged `replay: true`. */
export function replayTelemetry(target) {
    var e_1, _a;
    if (!installed) {
        return;
    }
    try {
        for (var replayBuffer_1 = __values(replayBuffer), replayBuffer_1_1 = replayBuffer_1.next(); !replayBuffer_1_1.done; replayBuffer_1_1 = replayBuffer_1.next()) {
            var message = replayBuffer_1_1.value;
            post(__assign(__assign({}, message), { replay: true }), target);
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (replayBuffer_1_1 && !replayBuffer_1_1.done && (_a = replayBuffer_1.return)) _a.call(replayBuffer_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
}
/** `iframe.error { message }`, truncated and rate-limited per load. */
export function emitTelemetryError(message) {
    var _a;
    if (!installed || errorsEmitted >= ERROR_LIMIT) {
        return;
    }
    errorsEmitted++;
    var text;
    if (message instanceof Error) {
        text = message.message || message.name;
    }
    else if (typeof message === "string") {
        text = message;
    }
    else {
        try {
            text = (_a = JSON.stringify(message)) !== null && _a !== void 0 ? _a : String(message);
        }
        catch (_b) {
            text = String(message);
        }
    }
    emitTelemetry("iframe.error", {
        message: text.slice(0, ERROR_MESSAGE_LIMIT),
    });
}
export function isTelemetryInstalled() {
    return installed;
}
function vramSnapshot() {
    var _a;
    var vram = (_a = app === null || app === void 0 ? void 0 : app.stats) === null || _a === void 0 ? void 0 : _a.vram;
    if (!vram) {
        return undefined;
    }
    return {
        tex: vram.tex,
        vb: vram.vb,
        ib: vram.ib,
        total: vram.tex + vram.vb + vram.ib,
    };
}
function drawCalls() {
    return lastDrawCalls;
}
function onContextLost(source) {
    if (contextLost) {
        return;
    }
    contextLost = true;
    emitTelemetry("iframe.webgl_context_lost", { source: source });
}
function onContextRestored(source) {
    if (!contextLost) {
        return;
    }
    contextLost = false;
    emitTelemetry("iframe.webgl_context_restored", { source: source });
}
/**
 * Install listeners and send `iframe.boot`. Call once, as early as possible
 * (before definitions/assets load), and only in embed mode.
 */
export function installEmbedTelemetry(options) {
    if (installed || typeof window === "undefined") {
        return;
    }
    installed = true;
    quality = options.quality;
    document.addEventListener("visibilitychange", function () {
        emitTelemetry("iframe.visibility", { state: document.visibilityState });
    });
    window.addEventListener("pagehide", function () {
        emitTelemetry("iframe.pagehide");
    });
    /* Page Lifecycle API (Chrome/Android; feature-detected). */
    if ("onfreeze" in document) {
        document.addEventListener("freeze", function () { return emitTelemetry("iframe.freeze"); });
        document.addEventListener("resume", function () { return emitTelemetry("iframe.resume"); });
    }
    window.addEventListener("error", function (event) {
        emitTelemetryError(event.message || event.error || "unknown error");
    });
    window.addEventListener("unhandledrejection", function (event) {
        var _a;
        emitTelemetryError((_a = event.reason) !== null && _a !== void 0 ? _a : "unhandled rejection without reason");
    });
    emitTelemetry("iframe.boot", {
        ua: navigator.userAgent,
        /* Protocol capabilities of this client build; lets the server-side event log tell a stale cached client apart. */
        caps: ["snapshot"],
        quality: quality,
        dpr: window.devicePixelRatio,
        viewport: { w: window.innerWidth, h: window.innerHeight },
        url: location.href,
    });
}
/** Hook the WebGL canvas's context-lost/restored events. */
export function attachTelemetryCanvas(canvas) {
    if (!installed) {
        return;
    }
    canvas.addEventListener("webglcontextlost", function () { return onContextLost("canvas"); });
    canvas.addEventListener("webglcontextrestored", function () {
        return onContextRestored("canvas");
    });
}
/**
 * Hook the PlayCanvas application: first rendered frame, device lost/restored,
 * and the 15 s `iframe.stats` ticker (only while the document is visible).
 * Call right after the application has been created/started.
 */
export function attachTelemetryApp(application) {
    if (!installed || app === application) {
        return;
    }
    app = application;
    var emitFirstFrame = function () {
        if (firstFrameEmitted) {
            return;
        }
        firstFrameEmitted = true;
        emitTelemetry("iframe.first_frame", { msSinceBoot: msSinceBoot() });
    };
    application.once("postrender", emitFirstFrame);
    /* This client renders on demand (autoRender off when idle), so the tick-based
     * `stats.drawCalls.total` is usually 0. Sample the device counter at the end
     * of each rendered frame instead; it is reset by the next tick, so several
     * direct renders within one tick accumulate. */
    application.on("postrender", function () {
        lastDrawCalls = application.graphicsDevice._drawCallsPerFrame;
    });
    var device = application.graphicsDevice;
    if (device && typeof device.on === "function") {
        device.on("devicelost", function () { return onContextLost("device"); });
        device.on("devicerestored", function () { return onContextRestored("device"); });
    }
    if (statsTimer === undefined) {
        statsTimer = setInterval(function () {
            var _a, _b;
            if (document.visibilityState !== "visible" || !app) {
                return;
            }
            emitTelemetry("iframe.stats", {
                vram: vramSnapshot(),
                drawCalls: drawCalls(),
                fps: (_b = (_a = app.stats) === null || _a === void 0 ? void 0 : _a.frame) === null || _b === void 0 ? void 0 : _b.fps,
            });
        }, STATS_INTERVAL_MS);
    }
}
/**
 * `iframe.scene_loaded`: call when the initial scene and product are built.
 * Emitted once per load.
 */
export function emitTelemetrySceneLoaded() {
    if (!installed || sceneLoadedEmitted) {
        return;
    }
    sceneLoadedEmitted = true;
    var device = app === null || app === void 0 ? void 0 : app.graphicsDevice;
    var textures = app
        ? app.assets.filter(function (asset) { return asset.type === "texture" && asset.loaded; })
            .length
        : undefined;
    emitTelemetry("iframe.scene_loaded", {
        msSinceBoot: msSinceBoot(),
        vram: vramSnapshot(),
        drawCalls: drawCalls(),
        textures: textures,
        dpr: window.devicePixelRatio,
        antialias: device === null || device === void 0 ? void 0 : device.backBufferAntialias,
        /* Additions beyond CONTRACT 7.1, for the quality A/B read-out. */
        quality: quality,
        maxPixelRatio: device === null || device === void 0 ? void 0 : device.maxPixelRatio,
        canvas: device ? { w: device.width, h: device.height } : undefined,
    });
}
